import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';

import MuiDialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';

interface Props {
  readonly cancelLabel?: string;
  readonly confirmLabel?: string;
  readonly content: ReactNode;
  readonly id: string;
  readonly isConfirmDisabled?: boolean;
  readonly isLoading?: boolean;
  readonly isVisible: boolean;
  readonly onCancel?: (() => void) | (() => Promise<unknown>);
  readonly onConfirm?: (() => void) | (() => Promise<unknown>);
  readonly setIsVisible: (newIsVisible: boolean) => void;
  readonly showCancel?: boolean;
  readonly showConfirm?: boolean;
  readonly title: string;
}

export const Dialog = ({
  cancelLabel = 'Annuler',
  confirmLabel = 'Valider',
  content,
  id,
  isConfirmDisabled,
  isLoading,
  isVisible,
  onCancel,
  onConfirm,
  setIsVisible,
  showCancel = true,
  showConfirm = true,
  title,
}: Readonly<Props>) => {
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const handleCancel = useCallback(async () => {
    if (onCancel) {
      setIsCancelLoading(true);
      await onCancel();
      setIsCancelLoading(false);
    }
    setIsVisible(false);
  }, [onCancel, setIsVisible]);

  const handleConfirm = useCallback(async () => {
    if (onConfirm) {
      setIsConfirmLoading(true);
      await onConfirm();
      setIsConfirmLoading(false);
    }
    setIsVisible(false);
  }, [onConfirm, setIsVisible]);

  return (
    <MuiDialog
      aria-describedby={`${id}-dialog-description`}
      aria-labelledby={`${id}-dialog-title`}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClose={handleCancel}
      open={isVisible}
    >
      <DialogTitle id={`${id}-dialog-title`}>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div" id={`${id}-dialog-description`}>
          {content}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!showConfirm && (
          <Button
            autoFocus
            disabled={!!isConfirmDisabled || !!isLoading || isCancelLoading}
            loading={isConfirmLoading}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleConfirm}
            size="large"
            variant="contained"
          >
            {confirmLabel}
          </Button>
        )}
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!showCancel && (
          <Button
            autoFocus={!showConfirm}
            disabled={isLoading ?? isConfirmLoading}
            loading={isCancelLoading}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleCancel}
            size="large"
            variant="outlined"
          >
            {cancelLabel}
          </Button>
        )}
      </DialogActions>
    </MuiDialog>
  );
};
